import { Deal } from './Deal';
import { DealDebtSchedule } from './DealDebtSchedule';
import { DealRequestAllocation } from './DealRequestAllocation';
import { ProfileRealEstateType } from './DealRequestAllocationRealEstate';
import { Guid } from './Shared';
import { SourceUse } from './SourceUse';

export interface DataRoomLoanApplication {
	dealGuid: string;
	dataRoomGuid: string;
	effectiveDate: Date;
	col0: string;
	col1: string;
	col2: string;
	col3NoHtml: string;
	col4NoHtml: string;
	col5NoHtml: string;
	showBorrowerMetrics: boolean;
	showAcquisitionMetrics: boolean;
	transactionManager: string;
	secondaryTransactionManager: string;
	lenderRelationsManager: string;
	deal: Deal;
	allocation: DealRequestAllocation;
	financialMetrics: DealFinancialMetric[];
	debtSchedules: DealDebtSchedule[];
	excludedLenders: DealExcludedLender[];
	reducedFeeLenders: DealReducedFeeLender[];
	tasks: LoanApplicationTask[];
	sources: SourceUse[];
	uses: SourceUse[];
	isRawLead: boolean;
	isRawLeadVisible: boolean;
}

export interface DealExcludedLender {
	guid: Guid;
	dealGuid: Guid;
	bankGuid: Guid;
	bankName: string;
	isIncumbentLender: boolean;
}

export interface DealReducedFeeLender {
	guid: Guid;
	dealGuid: Guid;
	bankGuid: Guid;
	bankName: string;
}

export interface DealFinancialMetric {
	guid: string;
	dealGuid: string;
	analysisMetricType?: DealAnalysisMetricType;
	financialMetricType?: DealFinancialMetricType;
	metricName: string;
	twoYearsAgo?: number;
	previousFullYear?: number;
	trailingTwelveMonths?: number;
	projectedFullYear?: number;
	futureYear?: number;
	amountDescription?: string;
	profileRealEstateType?: ProfileRealEstateType;
}

export type DealFinancialMetricType = keyof typeof DealFinancialMetricTypeMap;
export const DealFinancialMetricTypeMap = {
	Cash: 'Cash & Marketables',
	AccountsReceivables: 'Accounts Receivable',
	Inventory: 'Inventory',
	RealEstate: 'Owner Occupied Real Estate',
	FurnitureFixture: 'Furniture / Fixtures',
	Equipment: 'Machinery & Equipment',
	Vehicles: 'Rolling Stock/Vehicles',
	MarineVessels: 'Marine Vessels',
	Helicopters: 'Helicopters',
	FixedWingAircraft: 'Fixed Wing Aircraft',
	Royalties: 'Royalty/Licensing Agreements',
	IntellectualProperty: 'Intellectual Property',
	TotalRevenue: 'Total Revenue',
	NetIncome: 'Net Income',
	Ebitda: 'EBITDA',
	Adjustments: 'EBITDA Adjustments',
	AdjustedEbitda: 'Adjusted EBITDA',
	Mrr: 'MRR (most recent period)',
};

export type DealAnalysisMetricType = keyof typeof DealAnalysisMetricTypeMap;
export const DealAnalysisMetricTypeMap = {
	Borrower: 'Borrower',
	Acquisition: 'Acquisition',
};

export interface DealPreferenceItem {
	index: number;
	propertyName: 'PricePriority' | 'CovenantPriority' | 'MaturityPriority' | 'AmortizationPriority' | 'AmountPriority';
	displayName: string;
}

export interface LoanApplicationTask {
	guid: string;
	actionType: ApplicationActionType;
	taskType: LoanApplicationTaskType;
	creationTriggerType: LoanApplicationTaskCreationTriggerType;
	applicationTaskTitle: string;
	collectionIndex: number | null;
	notApplicable: boolean;
	heading: string;
	subHeading: string;
	displayOrder: number | null;
	completedOn: Date | null;
	completedBy: string | null;
	status: ApplicationTaskStatusType;
	documents: LoanApplicationTaskDocument[];
	createdOn: string;
	summary: string;
	loanApplicationDisplayOrder: number;
	statusValue: number;
}

export type ApplicationTaskStatusType = keyof typeof ApplicationTaskStatusTypeMap;
export const ApplicationTaskStatusTypeMap = {
	Pending: 'Pending',
	InProgress: 'In Progress',
	Completed: 'Completed',
	NotApplicable: 'N/A',
};

export type ApplicationActionType = keyof typeof ApplicationActionTypeMap;
export const ApplicationActionTypeMap = {
	Upload: 'Upload',
	Link: 'Link',
};

export type LoanApplicationTaskType = keyof typeof LoanApplicationTaskTypeMap;
export const LoanApplicationTaskTypeMap = {
	Custom: 'Custom',
	Form1919Section1: 'SBA Form 1919 Section I',
	Form1919Section2: 'SBA Form 1919 Section II',
	Form912: 'SBA Form 912',
	Form413: 'SBA Form 413',
	PersonalRealEstateTitleInsurance: 'Personal Real Estate - Title Insurance',
	PersonalRealEstateVerification: 'Personal Real Estate - Mortgage Verification',
	PersonalRealEstateHazardInsurance: 'Personal Real Estate - Hazard Insurance',
	PersonalRealEstateBrokerPriceOpinion: 'Personal Real Estate - Broker Price Opinion',
	PersonalRealEstateFloodCertifications: 'Personal Real Estate - Flood Certifications',
	PersonalCreditReport: 'Personal Credit Report',
	DerogatoryCreditItemExplanation: 'Explanation of Derogatory Credit Items',
	CopyDriversLicense: 'Copy of Drivers License',
	PersonalTaxReturn: 'Personal Tax Return',
	LastYearW2: 'Last Year W2',
	Form4506T: 'IRS Form 4506-T',
	MostRecentPayStub: 'Most Recent Pay Stubs',
	FranchiseDeclarationDocument: 'Copy of Franchise Declaration Document(s)',
	FranchisorApprovalLetter: 'Franchisor Letter of Approval',
	BusinessTaxReturns: 'Business Tax Returns (Last 3 Years)',
	PreparedCompanyFinancialStatements: 'Business/CPA Prepared Financial Statements',
	InterimBalanceSheet: 'Interim Balance Sheet',
	InterimIncomeStatement: 'Interim Income Statement',
	AccountsReceivableAgingReport: 'A/R Agining Report',
	AccountsPayableAgingReport: 'A/P Aging Report',
	EquipmentList: 'Equipment List',
	WorkingCapitalCertification: 'Working Capital Certification',
	CopyNotesToBeRefinanced: 'Copy of Notes to be Refinanced',
	NextTwelveMonthlyProjections: 'Next Twelve Monthly Projections',
	DetailNextTwelveMonthProjections: 'Detailed Assumptions for Next Twelve Month Projections',
	HistoryOfBusiness: 'History of Business',
	TinVerification: 'Organizational TIN Verification',
	OperatingAgreement: 'Organizational Operating Agreement',
	ArticlesOfOrganization: 'Organizational Articles of Organization',
	CertificateOfMembers: 'Organizational Certificate of Members',
	DbaRegistration: 'Organizational DBA Registration',
	CertificateOfGoodStanding: 'Organizational Certificate of Good Standing',
	SiteInspection: 'Site Inspection',
	FixtureFiling: 'Fixture Filing',
	CopyFullyExpectedLease: 'Lease Agreement - Copy of Full Expected Lease',
	AssignmentLeaseToLender: 'Lease Agreement - Assignment of Lease to Lender',
	LandlordWaiver: 'Lease Agreement - Landlord Waiver',
	AcquisitionPurchaseAgreement: 'Copy of Acquisition Purchase Agreement',
	AcquisitionSellerNote: 'Copy of Acquisition Seller Note',
	AcquisitionBusinessTaxReturns: 'Acquisition Business Tax Returns (Last 3 Years)',
	AcquisitionForm4506T: 'Acquisition IRS Form 4506-T',
	AcquisitionPreparedCompanyFinancialStatements: 'Acquisition Business/CPA Prepared Financial Statements',
	AcquisitionInterimBalanceSheet: 'Acquisition Interim Balance Sheet',
	AcquisitionInterimIncomeStatement: 'Acquisition Interim Income Statement',
	AcquisitionAccountsReceivableAgingReport: 'Acquisition A/R Aging Report',
	AcquisitionAccountsPayableAgingReport: 'Acquisition A/P Aging Report',
	AcquisitionEquipmentList: 'Acquisition Equipment List',
	RefinancePaymentHistory: 'Refinance Payment History',
	RefinancePayoffLetters: 'Refinance Payoff Letters',
	AffiliateBusinessTaxReturns: 'Affiliate Business Tax Returns (Last 3 Years)',
	AffiliateForm4506T: 'Affiliate IRS Form 4506-T',
	AffiliatePreparedCompanyFinancialStatements: 'Affiliate Business/CPA Prepared Financial Statements',
	AffiliateInterimBalanceSheet: 'Affiliate Interim Balance Sheet',
	AffiliateInterimIncomeStatement: 'Affiliate Interim Income Statement',
	AffiliateAccountsReceivableAgingReport: 'Affiliate A/R Aging Report',
	AffiliateAccountsPayableAgingReport: 'Affiliate A/P Aging Report',
	Form202: 'SBA Form 202',
	Form1919Section3: 'SBA Form 1919 Section III',
};

export type LoanApplicationTaskCreationTriggerType = keyof typeof LoanApplicationTaskCreationTriggerTypeMap;
export const LoanApplicationTaskCreationTriggerTypeMap = {
	Other: 'Other',
	DealStructureSelection: 'Deal Structure Selection',
	DealCreation: 'Deal Creation',
};

export interface LoanApplicationTaskDocument {
	guid: string;
	documentTitle: string;
	fileName: string;
	createdOn: Date;
	fileSize: number;
}

export interface LoanApplicationTaskTemplate {
	guid: string;
	actionType: ApplicationActionType;
	taskType: LoanApplicationTaskType;
	title: string;
	displayOrder: number;
	heading: string;
	subHeading: string;
	canBeAddedManually: boolean;
	canBeDeleted: boolean;
	isLoanApplicationDefault: boolean;
	isCustomFolder: boolean;
}
